import type { Hex, Lines, Song, Widget } from '../contract.js';
import { copy, mmss } from '../copy.js';
import { bgId, bgSpec, hexA } from '../grid/background.js';
import { boxOf, span } from '../grid/engine.js';
import type { Corner } from '../grid/engine.js';
import { TAP_TO_WRITE, fsStyle, paragraphs, textFit } from '../grid/text.js';
import type { AppState } from '../state/types.js';
import { color, ease, shadow } from '../tokens.js';

/** What each widget of a grid looks like, ported from `gridVals` (04 §1–§2, §7–§11; 02 §3.16–3.26). */

export type Surface = 'post' | 'profile';
export interface GridOptions {
  readonly editing: boolean;
  /** The grid owner's colour: outlines, own-colour backgrounds, the song's progress line. */
  readonly color: Hex;
  /** Prefix of playback and retry keys, so the same widget id in two places never collides. */
  readonly keyBase: string;
  /** Profile pop-in phase. */
  readonly enter: null | 'in-pre' | 'in';
  readonly surface: Surface;
  readonly lines: Lines | null | undefined;
}

export interface CornerVals { readonly id: Corner; readonly x: number; readonly y: number; readonly cursor: string; readonly ax: number; readonly ay: number; readonly bw: string; readonly br: string; readonly origin: string }

export interface WidgetVals {
  readonly id: string;
  readonly t: Widget['t'];
  readonly key: string;
  readonly x: number; readonly y: number; readonly w: number; readonly h: number;
  readonly z: number; readonly op: number; readonly tf: string; readonly tr: string;
  readonly bg: string; readonly shadow: string; readonly blur: string;
  readonly olW: string; readonly olC: string;
  /** Photo and video: the media box inside the widget. */
  readonly media: { readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly tr: string; readonly url: string; readonly alt: string; readonly brokenText: string;
    /** An animated picture (a GIF): the only kind that is held on hover or tap. */
    readonly anim: boolean } | null;
  readonly text: {
    readonly paras: readonly string[]; readonly cols: number; readonly fs: number; readonly lh: number; readonly fw: number; readonly ls: string; readonly color: string;
    /** The text is being written: a textarea replaces the paragraphs. */
    readonly writing: boolean; readonly value: string; readonly maxLen: number; readonly fieldBg: string;
  } | null;
  readonly song: {
    readonly song: Song | null; readonly canPlay: boolean; readonly offIcon: boolean; readonly playing: boolean; readonly playLabel: string;
    readonly title: string; readonly sub: string; readonly subColor: string; readonly barOp: number; readonly pct: string; readonly color: Hex; readonly len: number; readonly pos: number;
  } | null;
  /**
   * A video plays muted on its own (owner, 4 October 2026). `controls`: its controls can be shown (not while
   * its grid is edited). `compact`: below 2 × 2 cells only pause and sound, no timeline. `len`: seconds, a fallback
   * until the element knows its duration.
   */
  readonly video: { readonly controls: boolean; readonly compact: boolean; readonly len: number } | null;
  /** Editing affordances; null in view mode. */
  readonly edit: {
    readonly selected: boolean;
    readonly ringW: string; readonly ringC: string; readonly markC: string; readonly markOp: number;
    readonly grip: { readonly x: number; readonly y: number; readonly color: string } | null;
    readonly centre: { readonly w: number; readonly h: number; readonly cursor: string; readonly aria: string } | null;
    readonly corners: readonly CornerVals[];
  } | null;
}

const SNAP = `${['left', 'top', 'width', 'height'].map((p) => `${p} 180ms ${ease.out}`).join(', ')}, transform 200ms ${ease.out}`;
const BOX = ['left', 'top', 'width', 'height'].map((p) => `${p} 180ms ${ease.out}`).join(', ');
const PLAIN_LINE = 'rgba(233, 237, 245, 0.14)';
const T = '3px';

export function gridVals(st: AppState, items: readonly Widget[], o: GridOptions): WidgetVals[] {
  const ed = o.editing, g = ed ? st.pdrag : null;
  const lineC = o.lines === 'plain' ? PLAIN_LINE : hexA(o.color, 0.9);
  // Voice items (G12) are kept and keep their cells, but nothing is drawn for them and they cannot be selected.
  return items.filter((it) => it.t !== 'voice').map((it, i): WidgetVals => {
    const dg = g && g.id === it.id ? g : null, R = dg && dg.mode === 'resize' ? dg : it;
    const W = span(R.w), H = span(R.h), sel = ed && st.sel === it.id, writing = ed && st.txt === it.id && it.t === 'text';
    const pan = dg && dg.mode === 'pan' ? dg : null;
    const px = pan ? pan.px : it.px ?? 0.5, py = pan ? pan.py : it.py ?? 0.5, z = pan && pan.z != null ? pan.z : it.z || 1;
    const isMedia = it.t === 'photo' || it.t === 'video';
    const b = isMedia ? boxOf(it, W, H, px, py, z) : { x: 0, y: 0, w: 0, h: 0 };
    let op = 1, tf = 'none', tr = SNAP;
    const delay = `${60 + i * 45}ms`;
    if (dg && dg.mode === 'move') { tf = `translate(${dg.dx}px, ${dg.dy}px) scale(1.02)`; tr = 'none'; }
    else if (o.enter === 'in-pre') { op = 0; tf = 'scale(0.92)'; tr = 'none'; }
    else if (o.enter === 'in') tr = `opacity 240ms ease ${delay}, transform 380ms ${ease.popSoft} ${delay}`;

    const key = `${o.keyBase}:${it.id}`;
    const p = st.play && st.play.key === key ? st.play : null, playing = !!(p && !p.paused), pos = p ? p.pos : 0;
    const song = it.t === 'song' ? it.song ?? null : null, songOff = !song || !!song.off;
    const len = it.t === 'song' ? (song ? song.len : 1) : p && it.t === 'video' ? p.len : it.len || 10;

    let ft = it.t === 'text' ? textFit(it.text || (ed ? TAP_TO_WRITE : ''), W, H) : fsStyle(14);
    if (it.t === 'text' && it.fs) ft = fsStyle(it.fs, ft.cols);
    let paras = paragraphs(it.text);
    const empty = it.t === 'text' && !paras.length;
    if (empty && ed) paras = [copy.edit.tapToWrite];
    const bad = !!dg && dg.mode !== 'pan' && !dg.ok;
    const spec = it.t === 'text' ? bgSpec(bgId(it), o.color) : { css: color.tile, light: false, blur: false };
    const tc = empty ? (spec.light ? color.inkOnLightEmpty : color.textFaint) : spec.light ? color.inkOnLight : color.textStrong;

    const label = it.t === 'text' ? copy.edit.labelText(it.text ?? '') : it.t === 'song' ? copy.edit.labelSong(song ? song.t : '')
      : it.t === 'video' ? copy.widget.video(it.name) : copy.widget.photo(it.name);
    const how = it.t === 'text' ? copy.edit.howText : it.t === 'song' ? copy.edit.howSong : copy.edit.howMedia;
    const corner = (id: Corner, x: number, y: number, cursor: string, ax: number, ay: number, bw: string, br: string, origin: string): CornerVals =>
      ({ id, x, y, cursor, ax, ay, bw: bw.replace(/T/g, T), br, origin });
    const showCorners = sel || bad;
    const pct = `${Math.round(pos / Math.max(1, len) * 100)}%`;

    return {
      id: it.id, t: it.t, key,
      x: R.c * 82, y: R.r * 82, w: W, h: H, z: dg ? 8 : sel ? 4 : 1, op, tf, tr,
      bg: spec.css, shadow: dg && dg.mode === 'move' ? shadow.liftedWidget : 'none', blur: spec.blur ? 'blur(14px) saturate(1.3)' : 'none',
      olW: bad ? '2px' : '1.5px', olC: bad ? color.dangerStrong : sel ? color.white : lineC,
      media: isMedia ? {
        x: Math.round(b.x), y: Math.round(b.y), w: Math.round(b.w), h: Math.round(b.h), tr: pan ? 'none' : BOX, url: it.url || '',
        alt: it.t === 'video' ? copy.widget.video(it.name) : copy.widget.photo(it.name),
        brokenText: it.t === 'video' ? copy.widget.brokenVideo : copy.widget.brokenPhoto,
        anim: it.t === 'photo' && it.anim === true,
      } : null,
      text: it.t === 'text' ? {
        paras, cols: ft.cols, fs: ft.fs, lh: ft.lh, fw: ft.fw, ls: ft.ls, color: tc,
        writing, value: it.text || '', maxLen: o.surface === 'profile' ? 140 : 600, fieldBg: spec.light ? 'rgba(17, 20, 27, 0.06)' : 'rgba(255, 255, 255, 0.05)',
      } : null,
      song: it.t === 'song' ? {
        song, canPlay: !ed && !songOff, offIcon: ed || songOff, playing, playLabel: playing ? copy.widget.pauseSong(song ? song.t : '') : copy.widget.playSong(song ? song.t : ''),
        title: song ? song.t : '', sub: !song ? '' : song.off ? copy.widget.songOff : playing || pos ? `${mmss(pos)} / ${mmss(len)}` : song.a,
        subColor: song && song.off ? color.textFaint : color.textMuted, barOp: ed || songOff ? 0 : 1, pct, color: o.color, len, pos,
      } : null,
      video: it.t === 'video' ? { controls: !ed, compact: !(W >= 150 && H >= 120), len } : null,
      edit: ed ? {
        selected: sel,
        ringW: showCorners ? (bad ? '1.5px' : '1px') : '0px', ringC: bad ? color.dangerStrong : sel ? 'rgba(255, 255, 255, 0.35)' : 'rgba(255, 255, 255, 0.2)',
        markC: bad ? color.dangerStrong : color.white, markOp: bad || sel ? 1 : 0.55,
        grip: writing ? null : { x: Math.round(W / 2 - 36), y: H - 34, color: bad ? color.dangerStrong : sel ? color.white : 'rgba(255, 255, 255, 0.78)' },
        centre: writing ? null : {
          w: Math.max(0, W - 26), h: Math.max(0, H - 26), cursor: it.t === 'text' ? 'text' : it.t === 'song' ? 'move' : 'grab',
          aria: copy.edit.focus(label, R.w, R.h, R.c + 1, R.r + 1, how),
        },
        corners: showCorners ? [
          corner('nw', -18, -18, 'nwse-resize', 14, 14, 'T 0 0 T', '9px 0 0 0', '4px 4px'),
          corner('ne', W - 18, -18, 'nesw-resize', 6, 14, 'T T 0 0', '0 9px 0 0', '12px 4px'),
          corner('sw', -18, H - 18, 'nesw-resize', 14, 6, '0 0 T T', '0 0 0 9px', '4px 12px'),
          corner('se', W - 18, H - 18, 'nwse-resize', 6, 6, '0 T T 0', '0 0 9px 0', '12px 12px'),
        ] : [],
      } : null,
    };
  });
}
